import React, { useEffect, useState } from "react";
import {
    Typography,
    TextField,
    InputAdornment,
    IconButton,
    Button,
    TablePagination,
    Stack,
    Box,
    LinearProgress,
    Radio,
    RadioGroup,
    FormControl,
    FormLabel,
    FormControlLabel,
    Menu,
    Snackbar,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    DialogContentText
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import ClearIcon from "@mui/icons-material/Clear";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import { ThemeProvider, useTheme } from "@mui/material/styles";
import {
    tableTheme,
    tableConfigTheme,
    dropDownMenuTheme,
    paginationTheme
} from "../styles/ListViewTheme";
import PackageTable from "../components/PackageTable";
import PackageAddEditForm from "./PackageAddEditForm";
import * as mutations from "../graphql/mutations";
import { graphql, graphqlOperation } from "../modules/AmplifyServices";
import { getUserFromCache } from "../user/UserCommon";
import { auditPackagesDelete } from "../modules/Audit";
import { execReadByPK } from "../modules/DBService";
import { packageByCompany } from "../queries/CustomQueries";

const ROWS_PER_PAGE_OPTIONS = [25, 50, 100];

const STATUS_OPTIONS = { ALL: "all", ACTIVE: "active", INACTIVE: "inactive" };
const STATUS_LABELS = {
    [STATUS_OPTIONS.ALL]: "All",
    [STATUS_OPTIONS.ACTIVE]: "Active",
    [STATUS_OPTIONS.INACTIVE]: "Inactive"
};

function PackageForm() {
    const theme = useTheme();
    const authuser = getUserFromCache();

    // Data state
    const [rows, setRows] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    // Filter state
    const [statusFilter, setStatusFilter] = useState(STATUS_OPTIONS.ACTIVE);
    const [searchTerm, setSearchTerm] = useState("");

    // Pagination state
    const [page, setPage] = useState(0);
    const [perPage, setPerPage] = useState(25);

    // Sort state
    const [sortBy, setSortBy] = useState(null);
    const [sortDirection, setSortDirection] = useState("ASC");

    // Dialog state
    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogMode, setDialogMode] = useState("Add");
    const [editPackageId, setEditPackageId] = useState(null);
    const [editServiceId, setEditServiceId] = useState(null);

    // Snackbar state
    const [msgOpen, setMsgOpen] = useState(false);
    const [snackMsg, setSnackMsg] = useState("");

    // Delete state
    const [deleteDialog, setDeleteDialog] = useState(false);

    useEffect(() => {
        fetchPackagesList({ showLoading: rows.length === 0 });
    }, [statusFilter]);

    async function fetchPackagesList({ showLoading = false } = {}) {
        if (showLoading) setIsLoading(true);
        try {
            const filter = {
                and: [
                    { deleted: { ne: true } },
                    ...(statusFilter !== STATUS_OPTIONS.ALL
                        ? [
                              {
                                  active: {
                                      eq:
                                          statusFilter ===
                                          STATUS_OPTIONS.ACTIVE
                                  }
                              }
                          ]
                        : [])
                ]
            };
            let listPackagesData = await execReadByPK({
                opname: "packageByCompany",
                op: packageByCompany,
                id: { companyId: authuser.company.id },
                filter,
                sortDirection: "DESC"
            });

            listPackagesData.items.sort((p1, p2) => {
                const psq1 = (p1.servicetype?.name || "").toUpperCase();
                const psq2 = (p2.servicetype?.name || "").toUpperCase();
                if (psq1 === psq2) return p1.quantity - p2.quantity;
                if (psq1 < psq2) return -1;
                if (psq1 > psq2) return 1;
                return 0;
            });
            setRows(listPackagesData.items);
        } catch (e) {
            console.log(e);
        }
        setIsLoading(false);
    }

    // Client-side search filtering
    const filteredRows = searchTerm
        ? rows.filter((item) => {
              const terms = searchTerm.toLowerCase().split(" ");
              return terms.every(
                  (t) =>
                      (item.servicetype?.name || "")
                          .toLowerCase()
                          .includes(t) ||
                      (item.desc || "").toLowerCase().includes(t)
              );
          })
        : rows;

    // Sorting
    const sortedRows = sortBy
        ? [...filteredRows].sort((a, b) => {
              let aVal, bVal;
              switch (sortBy) {
                  case "serviceName":
                      aVal = (a.servicetype?.name || "").toLowerCase();
                      bVal = (b.servicetype?.name || "").toLowerCase();
                      break;
                  case "desc":
                      aVal = (a.desc || "").toLowerCase();
                      bVal = (b.desc || "").toLowerCase();
                      break;
                  case "quantity":
                      aVal = a.quantity || 0;
                      bVal = b.quantity || 0;
                      break;
                  case "packagetype":
                      aVal = (a.packagetype || "").toLowerCase();
                      bVal = (b.packagetype || "").toLowerCase();
                      break;
                  case "price":
                      aVal =
                          a.packagetype === "DOLLAR"
                              ? a.price || 0
                              : a.discount || 0;
                      bVal =
                          b.packagetype === "DOLLAR"
                              ? b.price || 0
                              : b.discount || 0;
                      break;
                  default:
                      return 0;
              }
              if (sortDirection === "ASC") {
                  return aVal > bVal ? 1 : aVal < bVal ? -1 : 0;
              }
              return aVal < bVal ? 1 : aVal > bVal ? -1 : 0;
          })
        : filteredRows;

    // Pagination
    const paginatedRows = sortedRows.slice(
        page * perPage,
        page * perPage + perPage
    );

    function handlePackageAddClick() {
        setDialogMode("Add");
        setEditPackageId(null);
        setDialogOpen(true);
    }

    function handleEditPackage(id, serviceId) {
        setDialogMode("Edit");
        setEditPackageId(id);
        setEditServiceId(serviceId);
        setDialogOpen(true);
    }

    function handleDialogClose() {
        setDialogOpen(false);
        fetchPackagesList();
    }

    const handlePageChange = (event, newPage) => {
        setPage(newPage);
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const handleSort = (column) => {
        if (sortBy === column) {
            setSortDirection(sortDirection === "ASC" ? "DESC" : "ASC");
        } else {
            setSortBy(column);
            setSortDirection("ASC");
        }
    };

    const deletePackage = async (id) => {
        const input = { id, deleted: true };
        try {
            let result = await graphql(
                graphqlOperation(mutations.updatePackage, { input })
            );
            await auditPackagesDelete(authuser, result.data.updatePackage);
            if (result.data?.updatePackage) {
                fetchPackagesList();
                setDeleteDialog(false);
            } else {
                setSnackMsg("Something went wrong while deleting package.");
                setMsgOpen(true);
            }
        } catch (err) {
            setSnackMsg("Something went wrong while deleting package.");
            setMsgOpen(true);
        }
    };

    return (
        <>
            <Snackbar
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
                open={msgOpen}
                autoHideDuration={3000}
                onClose={() => setMsgOpen(false)}
                ContentProps={{ "aria-describedby": "message-id" }}
                message={<span id="message-id">{snackMsg}</span>}
            />
            <Dialog
                open={deleteDialog}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogTitle id="alert-dialog-title">
                    Are you sure you wish to delete the selected packages?
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        You are about to delete all the selected packages. They
                        will no longer be available on the system.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setDeleteDialog(false)}
                        color="primary"
                    >
                        Cancel
                    </Button>
                    <Button
                        color="secondary"
                        autoFocus
                        onClick={() => deletePackage(editPackageId)}
                    >
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            {isLoading ? (
                <Box sx={{ width: "100%", paddingTop: "18px" }}>
                    <LinearProgress />
                </Box>
            ) : (
                <ThemeProvider theme={tableTheme(theme)}>
                    <PackageTableHeader
                        statusFilter={statusFilter}
                        setStatusFilter={(val) => {
                            setStatusFilter(val);
                            setPage(0);
                        }}
                        searchTerm={searchTerm}
                        setSearchTerm={(val) => {
                            setSearchTerm(val);
                            setPage(0);
                        }}
                        onAddClick={handlePackageAddClick}
                    />
                    <Box
                        sx={{
                            flexGrow: 1,
                            display: "flex",
                            flexDirection: "column"
                        }}
                    >
                        {paginatedRows.length === 0 ? (
                            <>
                                <PackageTable
                                    packages={[]}
                                    sortBy={sortBy}
                                    sortDirection={sortDirection}
                                    onSort={handleSort}
                                    onEditPackage={handleEditPackage}
                                />
                                <Box
                                    sx={{
                                        flexGrow: 1,
                                        display: "flex",
                                        justifyContent: "center",
                                        alignItems: "center",
                                        height: "70vh"
                                    }}
                                >
                                    <Typography
                                        variant="body1"
                                        color={theme.palette.text.secondary}
                                    >
                                        No packages found
                                    </Typography>
                                </Box>
                            </>
                        ) : (
                            <PackageTable
                                packages={paginatedRows}
                                sortBy={sortBy}
                                sortDirection={sortDirection}
                                onSort={handleSort}
                                onEditPackage={handleEditPackage}
                            />
                        )}
                    </Box>
                    <ThemeProvider theme={paginationTheme(theme)}>
                        <TablePagination
                            count={sortedRows.length}
                            component="div"
                            page={page}
                            rowsPerPage={perPage}
                            onPageChange={handlePageChange}
                            onRowsPerPageChange={({
                                target: { value }
                            }) => {
                                setPerPage(parseInt(value, 10));
                                setPage(0);
                            }}
                            rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
                            labelRowsPerPage=""
                        />
                    </ThemeProvider>
                </ThemeProvider>
            )}

            <PackageAddEditForm
                open={dialogOpen}
                onClose={handleDialogClose}
                mode={dialogMode}
                packageId={editPackageId}
                serviceId={editServiceId}
            />
        </>
    );
}

function PackageTableHeader({
    statusFilter,
    setStatusFilter,
    searchTerm,
    setSearchTerm,
    onAddClick
}) {
    const themePalette = useTheme().palette;

    return (
        <ThemeProvider theme={tableConfigTheme(themePalette)}>
            <Stack
                direction="row"
                justifyContent="flex-end"
                alignItems="center"
                sx={{ marginBottom: "15px" }}
            >
                <Stack
                    display="flex"
                    direction="row"
                    justifyContent="flex-end"
                    alignItems="center"
                    gap="18px"
                >
                    <PackageFiltersMenu
                        statusFilter={statusFilter}
                        setStatusFilter={setStatusFilter}
                    />
                    <TextField
                        id="search"
                        sx={{
                            maxWidth: "200px",
                            "& .MuiInputBase-root": {
                                paddingLeft: "14px !important"
                            }
                        }}
                        value={searchTerm}
                        placeholder="Search"
                        onChange={({ target: { value } }) =>
                            setSearchTerm(value)
                        }
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon
                                        sx={{
                                            width: "18px",
                                            height: "18px",
                                            color: `${themePalette.helperText.main}`
                                        }}
                                    />
                                </InputAdornment>
                            ),
                            endAdornment: searchTerm &&
                                searchTerm.length > 0 && (
                                    <InputAdornment position="end">
                                        <IconButton
                                            size="small"
                                            aria-label="clear"
                                            onClick={() => setSearchTerm("")}
                                        >
                                            <ClearIcon
                                                sx={{
                                                    width: "18px",
                                                    height: "18px"
                                                }}
                                            />
                                        </IconButton>
                                    </InputAdornment>
                                )
                        }}
                    />
                    <Button
                        textTransform="uppercase"
                        variant="contained"
                        color="primary"
                        startIcon={<AddIcon sx={{ color: "white" }} />}
                        onClick={onAddClick}
                    >
                        <b>Add Package</b>
                    </Button>
                </Stack>
            </Stack>
        </ThemeProvider>
    );
}

function PackageFiltersMenu({ statusFilter, setStatusFilter }) {
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);
    const theme = useTheme();

    return (
        <>
            <TextField
                sx={{ maxWidth: "150px" }}
                value={"Filters"}
                onClick={(e) => setAnchorEl(e.currentTarget)}
                fullWidth
                InputProps={{
                    sx: {
                        fontSize: "14px",
                        color: theme.palette.text.main,
                        ...(open && {
                            "& fieldset": {
                                border: `2px solid ${theme.palette.primary.main}`
                            }
                        })
                    },
                    endAdornment: (
                        <InputAdornment position="start">
                            {open ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
                        </InputAdornment>
                    ),
                    readOnly: true
                }}
            />
            <ThemeProvider theme={dropDownMenuTheme(theme)}>
                <Menu
                    id="package-filters-menu"
                    anchorEl={anchorEl}
                    open={open}
                    fullWidth
                    onClick={() => setAnchorEl(null)}
                    onClose={() => setAnchorEl(null)}
                    MenuListProps={{
                        "aria-labelledby": "basic-button",
                        sx: {
                            width: anchorEl && anchorEl.offsetWidth,
                            padding: "10px 15px"
                        }
                    }}
                >
                    <FormControl>
                        <FormLabel>
                            <Typography variant="subHeading">Status</Typography>
                        </FormLabel>
                        <RadioGroup
                            defaultValue={STATUS_OPTIONS.ACTIVE}
                            value={statusFilter}
                            onChange={({ target: { value } }) =>
                                setStatusFilter(value)
                            }
                        >
                            {Object.values(STATUS_OPTIONS).map((option) => (
                                <FormControlLabel
                                    key={option}
                                    value={option}
                                    control={<Radio disableRipple />}
                                    label={STATUS_LABELS[option]}
                                />
                            ))}
                        </RadioGroup>
                    </FormControl>
                </Menu>
            </ThemeProvider>
        </>
    );
}

export default PackageForm;
